import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { useCreateBlankClientPresentationMutation } from '../graphql/CreateBlankClientPresentation.generated';

interface UseCreatePresentationOptions {
  navigateOnCreate?: boolean;
}

export function useCreatePresentation(
  householdId: string,
  { navigateOnCreate = true }: UseCreatePresentationOptions = {}
) {
  const navigate = useNavigate();
  const trackUserEvent = useTrackUserEvent();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const [createBlankClientPresentation, { loading }] =
    useCreateBlankClientPresentationMutation({
      onError: (err) => {
        reportError('Failed to create presentation', err);
        showFeedback('Failed to create a new presentation; please try again');
      },
      onCompleted: (data) => {
        trackUserEvent('presentation created');
        if (navigateOnCreate) {
          navigate(
            getCompletePathFromRouteKey(
              ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
              {
                householdId,
                presentationId: data.createBlankClientPresentation.id,
              }
            )
          );
        }
      },
    });

  const createPresentation = useCallback(
    () =>
      createBlankClientPresentation({
        variables: { householdID: householdId },
      }),
    [householdId, createBlankClientPresentation]
  );

  return { createPresentation, loading };
}
